import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { SelectionCard } from '../components/SelectionCard';
import { WALLS, DEFAULT_WALLS } from '../types/lanesAndDrivers';
import { supabase } from '../lib/supabase';

export function YourWalls() {
  const navigate = useNavigate();
  const [selectedWalls, setSelectedWalls] = useState<string[]>([]);
  const [customWall, setCustomWall] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadExistingWalls();
  }, []);

  async function loadExistingWalls() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('lane_1, lane_2, lane_3, lane_4')
      .eq('id', user.id)
      .maybeSingle();

    if (profile) {
      const walls = [profile.lane_1, profile.lane_2, profile.lane_3, profile.lane_4].filter(Boolean);
      if (walls.length > 0) {
        setSelectedWalls(walls);
      }
    }
  }

  function handleWallToggle(wallId: string) {
    if (selectedWalls.includes(wallId)) {
      setSelectedWalls(selectedWalls.filter(id => id !== wallId));
    } else if (selectedWalls.length < 4) {
      setSelectedWalls([...selectedWalls, wallId]);
    }
  }

  function handleCustomWallAdd() {
    if (customWall.trim() && selectedWalls.length < 4) {
      setSelectedWalls([...selectedWalls, customWall.trim().toLowerCase()]);
      setCustomWall('');
    }
  }

  function isWallDisabled(wallId: string) {
    return selectedWalls.length >= 4 && !selectedWalls.includes(wallId);
  }

  async function handleContinue() {
    if (selectedWalls.length !== 4) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update({
          lane_1: selectedWalls[0],
          lane_2: selectedWalls[1],
          lane_3: selectedWalls[2],
          lane_4: selectedWalls[3],
        })
        .eq('id', user.id);

      if (error) throw error;

      navigate('/clarify-definitions');
    } catch (error) {
      console.error('Error saving walls:', error);
      alert('Failed to save your walls. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Home className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Four Walls
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Like the four walls of a house, these represent the foundational areas you want to build your life around. Select four areas where you want to focus and grow.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
              <span className="font-semibold">Selected: {selectedWalls.length}/4</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Options</h2>

            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {WALLS.map((wall) => (
                <SelectionCard
                  key={wall.id}
                  icon={wall.icon}
                  title={wall.name}
                  selected={selectedWalls.includes(wall.id)}
                  onClick={() => handleWallToggle(wall.id)}
                  disabled={isWallDisabled(wall.id)}
                />
              ))}
            </div>

            <div className="mt-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Other Wall (Custom)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customWall}
                  onChange={(e) => setCustomWall(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleCustomWallAdd();
                  }}
                  placeholder="Enter custom wall..."
                  className="flex-1 p-2 rounded border border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600"
                  disabled={selectedWalls.length >= 4}
                />
                <Button
                  onClick={handleCustomWallAdd}
                  disabled={!customWall.trim() || selectedWalls.length >= 4}
                  variant="secondary"
                >
                  Add
                </Button>
              </div>
            </div>

            {selectedWalls.length === 4 && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 text-center font-medium">
                  Great choices! You've selected: {selectedWalls.map(id =>
                    WALLS.find(w => w.id === id)?.name || id
                  ).join(', ')}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button
              onClick={() => navigate('/dashgrowthloop')}
              variant="secondary"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <Button
              onClick={handleContinue}
              disabled={selectedWalls.length !== 4 || loading}
            >
              {loading ? 'Saving...' : 'Continue'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
